/**
 * Editor de campos calculados en un modal amplio: resaltado de sintaxis, autocompletado de
 * campos y funciones, tooltip con los parámetros de la función bajo el cursor, validación en
 * vivo con el error subrayado, catálogo de funciones con ejemplos y vista previa sobre los
 * datos reales. El lenguaje es estilo Tableau (IF/THEN/ELSE/END, CASE, {FIXED …}); la
 * validación y la vista previa las hace el motor (formulaApi). Cada campo muestra la hoja de
 * la que viene (fieldOrigins), con el mismo color que en el mapa de uniones.
 */
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ink, studio } from '../studioUi';
import { AGG_OPTIONS } from './engineConfig';
import { loadFormulaCatalog, previewFormula, validateFormula } from './formulaApi';
import {
    AGG_FN_FOR,
    KEYWORDS,
    TYPE_LABEL,
    callContextAt,
    completionContextAt,
    extractAggCalls,
    fieldReference,
    fnKey,
    formulaFieldRefs,
    formulaHasAggregates,
    indexCatalog,
    insertionFor,
    replaceNthAggFn,
    tokenizeFormula,
} from './formulaLanguage';

const MONO = "ui-monospace, 'Cascadia Code', 'Cascadia Mono', Consolas, 'Courier New', monospace";
const TYPE_BADGE = { number: '#', string: 'Abc', date: 'Fecha', boolean: 'V/F', any: '·' };
const GROUP_LABEL = { dimension: 'Dimensiones', metric: 'Métricas', calc: 'Campos calculados' };
const ROLE_TAG = { dimension: 'Dim', metric: 'Mét' };

/** Nombres de las hojas de un campo: «Ventas», «Ventas y Metas». */
function originText(origins = []) {
    return origins.map(origin => origin.label).join(' y ');
}

function OriginDots({ origins = [] }) {
    return origins.map(origin => (
        <span key={origin.id} title={origin.label} style={{ ...ui.originDot, backgroundColor: origin.color }} />
    ));
}
const TOKEN_COLORS = {
    kw: { color: ink.accent, fontWeight: 700 },
    fn: { color: '#1a5fb4', fontWeight: 600 },
    field: { color: '#0b6e4f', backgroundColor: '#e6f4ea', borderRadius: 3 },
    str: { color: '#a50e0e' },
    num: { color: '#b05a00' },
    date: { color: '#b05a00' },
    comment: { color: ink.faint, fontStyle: 'italic' },
    bad: { color: ink.danger },
};
const PAIRS = { '(': ')', '[': ']', '{': '}' };
const MIRROR_PROPS = [
    'boxSizing', 'width', 'borderTopWidth', 'borderRightWidth', 'borderBottomWidth', 'borderLeftWidth',
    'borderStyle', 'paddingTop', 'paddingRight', 'paddingBottom', 'paddingLeft', 'fontStyle', 'fontVariant',
    'fontWeight', 'fontStretch', 'fontSize', 'lineHeight', 'fontFamily', 'textAlign', 'textTransform',
    'textIndent', 'letterSpacing', 'wordSpacing', 'tabSize', 'whiteSpace', 'wordWrap', 'wordBreak',
];

/** Posición en pantalla (fixed) del carácter `position` de un textarea. */
function caretPoint(textarea, position) {
    const style = window.getComputedStyle(textarea);
    const mirror = document.createElement('div');
    MIRROR_PROPS.forEach(prop => { mirror.style[prop] = style[prop]; });
    Object.assign(mirror.style, {
        position: 'absolute', visibility: 'hidden', top: '0', left: '-9999px', height: 'auto', overflow: 'hidden',
    });
    mirror.textContent = textarea.value.slice(0, position);
    const marker = document.createElement('span');
    marker.textContent = textarea.value.slice(position) || '.';
    mirror.appendChild(marker);
    document.body.appendChild(mirror);
    const rect = textarea.getBoundingClientRect();
    const lineHeight = parseFloat(style.lineHeight) || 20;
    const point = {
        left: rect.left + (parseFloat(style.borderLeftWidth) || 0) + marker.offsetLeft - textarea.scrollLeft,
        top: rect.top + (parseFloat(style.borderTopWidth) || 0) + marker.offsetTop - textarea.scrollTop,
        lineHeight,
        editorTop: rect.top,
        editorBottom: rect.bottom,
    };
    document.body.removeChild(mirror);
    return point;
}

/** Inserta texto en el textarea conservando el deshacer del navegador cuando es posible. */
function replaceRange(textarea, from, to, text, caret) {
    textarea.focus();
    textarea.setSelectionRange(from, to);
    let done = false;
    try {
        done = document.execCommand('insertText', false, text);
    } catch (error) {
        done = false;
    }
    if (!done) {
        textarea.setRangeText(text, from, to, 'end');
        textarea.dispatchEvent(new Event('input', { bubbles: true }));
    }
    const target = from + (caret == null ? text.length : caret);
    textarea.setSelectionRange(target, target);
}

function renderHighlighted(formula, error) {
    const tokens = tokenizeFormula(formula);
    const errStart = error ? error.start : -1;
    const errEnd = error ? Math.max(error.end, error.start + 1) : -1;
    const pieces = [];
    const pushPiece = (text, style, key, isError) => {
        if (!text) return;
        pieces.push(
            <span key={key} style={isError ? { ...style, ...ui.errorMark } : style}>{text}</span>
        );
    };
    tokens.forEach((tok, index) => {
        const style = TOKEN_COLORS[tok.type] || (tok.type === 'op' ? { color: ink.text } : undefined);
        if (errStart < 0 || tok.end <= errStart || tok.start >= errEnd) {
            pushPiece(tok.text, style, index, false);
            return;
        }
        const a = Math.max(errStart, tok.start) - tok.start;
        const b = Math.min(errEnd, tok.end) - tok.start;
        pushPiece(tok.text.slice(0, a), style, `${index}a`, false);
        pushPiece(tok.text.slice(a, b), style, `${index}b`, true);
        pushPiece(tok.text.slice(b), style, `${index}c`, false);
    });
    if (error && errStart >= formula.length) {
        pieces.push(<span key="eof" style={ui.errorMark}>{' '}</span>);
    }
    // El salto final evita que el último renglón vacío desalinee el resaltado con el textarea.
    pieces.push(<span key="tail">{'\n'}</span>);
    return pieces;
}

function formatCell(value) {
    if (value === null || value === undefined || value === '') return <span style={ui.nullCell}>vacío</span>;
    if (typeof value === 'boolean') return value ? 'Verdadero' : 'Falso';
    if (typeof value === 'number') return value.toLocaleString('es-MX', { maximumFractionDigits: 4 });
    return String(value);
}

function Signature({ entry, argIndex }) {
    if (!entry) return null;
    const params = entry.params || [];
    const active = params.length
        ? Math.min(argIndex, params.length - 1)
        : -1;
    const activeParam = params[active] && (argIndex < params.length || params[params.length - 1]?.variadic)
        ? params[active]
        : null;
    return (
        <div>
            <div style={ui.sigLine}>
                {entry.kind === 'syntax' && entry.syntax ? (
                    <span>{entry.syntax}</span>
                ) : (
                    <>
                        <span style={{ fontWeight: 700 }}>{entry.name}</span>(
                        {params.map((param, i) => (
                            <React.Fragment key={param.name}>
                                {i > 0 && ', '}
                                <span style={i === active ? ui.sigActive : undefined}>
                                    {param.optional ? `[${param.name}]` : param.name}{param.variadic ? '…' : ''}
                                </span>
                            </React.Fragment>
                        ))}
                        )
                    </>
                )}
            </div>
            {activeParam && (
                <div style={ui.sigParam}><strong>{activeParam.name}:</strong> {activeParam.desc}</div>
            )}
            <div style={ui.sigDesc}>{entry.description}</div>
        </div>
    );
}

const FormulaEditorModal = ({
    initial,
    fields = [],
    takenNames = [],
    calculatedFields = [],
    previewSource,
    source,
    onSave,
    onClose,
}) => {
    const editing = Boolean(initial?.id);
    const [name, setName] = useState(initial?.name || '');
    const [formula, setFormula] = useState(initial?.formula || '');
    const [cursor, setCursor] = useState((initial?.formula || '').length);
    const [defaultAgg, setDefaultAgg] = useState(initial?.defaultAgg || 'sum');
    const [aggregations, setAggregations] = useState(initial?.aggregations || {});
    const [afterGrouping, setAfterGrouping] = useState(
        initial?.level === 'agg' && !formulaHasAggregates(initial?.formula || '')
    );
    const [catalog, setCatalog] = useState(null);
    const [catalogError, setCatalogError] = useState('');
    const [validation, setValidation] = useState({ status: 'idle', result: null });
    const [completionOpen, setCompletionOpen] = useState(false);
    const [forcedCompletion, setForcedCompletion] = useState(false);
    const [activeItem, setActiveItem] = useState(0);
    const [caret, setCaret] = useState(null);
    const [panelTab, setPanelTab] = useState('functions');
    const [panelSearch, setPanelSearch] = useState('');
    const [detail, setDetail] = useState(null);
    const [previewBy, setPreviewBy] = useState('');
    const [preview, setPreview] = useState({ status: 'idle', data: null, error: '' });
    const [saveError, setSaveError] = useState('');
    const textareaRef = useRef(null);
    const highlightRef = useRef(null);
    const sigRef = useRef(null);
    const [sigHeight, setSigHeight] = useState(0);
    const titleId = 'formula-editor-title';

    const catalogIndex = useMemo(() => indexCatalog(catalog), [catalog]);
    const fieldNames = useMemo(() => fields.map(field => field.name), [fields]);
    // La clave evita revalidar cuando el padre vuelve a crear la misma lista de campos.
    const fieldsKey = JSON.stringify(fields.map(field => [field.name, field.type || 'string']));
    const validationFields = useMemo(
        () => JSON.parse(fieldsKey).map(([fieldName, type]) => ({ name: fieldName, type })),
        [fieldsKey]
    );
    const dimensionFields = useMemo(() => fields.filter(field => field.group === 'dimension'), [fields]);

    const loadCatalog = useCallback(() => {
        setCatalogError('');
        loadFormulaCatalog()
            .then(setCatalog)
            .catch(error => setCatalogError(error.message || 'No se pudo cargar el catálogo de funciones.'));
    }, []);

    useEffect(() => { loadCatalog(); }, [loadCatalog]);

    useEffect(() => {
        const el = textareaRef.current;
        if (!el) return;
        el.focus();
        const end = el.value.length;
        el.setSelectionRange(end, end);
    }, []);

    // Validación en el motor con retraso corto; una respuesta vieja no pisa a la nueva.
    useEffect(() => {
        if (!formula.trim()) {
            setValidation({ status: 'idle', result: null });
            return undefined;
        }
        const controller = new AbortController();
        setValidation(prev => ({ status: 'checking', result: prev.result }));
        const timer = setTimeout(() => {
            validateFormula(formula, validationFields, { signal: controller.signal })
                .then(result => setValidation({ status: result.ok ? 'ok' : 'error', result }))
                .catch(error => {
                    if (error.name === 'AbortError') return;
                    setValidation({ status: 'offline', result: null });
                });
        }, 280);
        return () => {
            clearTimeout(timer);
            controller.abort();
        };
    }, [formula, validationFields]);

    const result = validation.result;
    const hasAggregates = validation.status === 'ok' ? result.level === 'agg' : formulaHasAggregates(formula);
    const level = hasAggregates || afterGrouping ? 'agg' : 'row';
    const resultType = validation.status === 'ok' ? result.type : null;
    const aggCalls = useMemo(() => extractAggCalls(formula), [formula]);
    const bareRefs = useMemo(
        () => (afterGrouping && !hasAggregates ? formulaFieldRefs(formula, fieldNames) : []),
        [afterGrouping, hasAggregates, formula, fieldNames]
    );
    const errorRange = validation.status === 'error' ? result.error : null;

    // Vista previa: se recalcula sola cuando la fórmula válida deja de cambiar.
    const previewKey = validation.status === 'ok'
        ? JSON.stringify([formula, level, previewBy, aggregations, afterGrouping])
        : '';
    const runPreview = useCallback(() => {
        if (!previewSource) return;
        setPreview(prev => ({ status: 'loading', data: prev.data, error: '' }));
        previewFormula({
            ...previewSource,
            formula,
            name: name.trim() || 'Resultado',
            calculated_fields: calculatedFields,
            aggregations: afterGrouping && !hasAggregates ? aggregations : {},
            by: level === 'agg' && previewBy ? previewBy : null,
            limit: 10,
        })
            .then(data => {
                if (data.ok === false) {
                    setPreview({ status: 'error', data: null, error: data.error?.message || 'La fórmula tiene un error.' });
                } else {
                    setPreview({ status: 'ok', data, error: '' });
                }
            })
            .catch(error => setPreview({ status: 'error', data: null, error: error.message }));
    }, [previewSource, formula, name, calculatedFields, afterGrouping, hasAggregates, aggregations, level, previewBy]);

    useEffect(() => {
        if (!previewKey) return undefined;
        const timer = setTimeout(runPreview, 700);
        return () => clearTimeout(timer);
        // runPreview cambia con las mismas entradas que previewKey.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [previewKey]);

    // ── Autocompletado y tooltip de parámetros ──
    const completion = useMemo(() => {
        if (!completionOpen) return { items: [], ctx: null };
        const ctx = completionContextAt(formula, cursor);
        if (!ctx) return { items: [], ctx: null };
        const q = ctx.query.toLowerCase();
        const qKey = fnKey(ctx.query);
        if (ctx.mode === 'word' && !q && !forcedCompletion) return { items: [], ctx };
        const fieldItems = fields
            .filter(field => !q || field.name.toLowerCase().includes(q) || String(field.label || '').toLowerCase().includes(q))
            .map(field => ({
                key: `f:${field.name}`,
                kind: 'field',
                label: field.label && field.label !== field.name ? `${field.label} (${field.name})` : field.name,
                detail: [TYPE_LABEL[field.type] || 'Campo', originText(field.origins)].filter(Boolean).join(' · '),
                rank: field.name.toLowerCase().startsWith(q) ? 1 : 2,
                field,
            }));
        if (ctx.mode === 'field') return { items: fieldItems.slice(0, 14), ctx };
        const fnItems = (catalog?.functions || [])
            .filter(entry => entry.kind !== 'syntax' || entry.insert)
            .filter(entry => !qKey || [entry.name, ...(entry.aliases || [])].some(alias => fnKey(alias).startsWith(qKey)))
            .map(entry => ({
                key: `fn:${entry.name}`,
                kind: entry.kind === 'syntax' ? 'syntax' : 'fn',
                label: entry.name,
                detail: entry.description,
                rank: 0,
                entry,
            }));
        const covered = new Set(fnItems.map(item => item.label));
        const kwItems = KEYWORDS
            .filter(kw => qKey && kw.startsWith(qKey) && !covered.has(kw))
            .map(kw => ({ key: `kw:${kw}`, kind: 'kw', label: kw, detail: 'Palabra clave', rank: 0 }));
        const items = [...fnItems, ...kwItems, ...fieldItems]
            .sort((a, b) => a.rank - b.rank || a.label.localeCompare(b.label, 'es'))
            .slice(0, 14);
        return { items, ctx };
    }, [completionOpen, forcedCompletion, formula, cursor, fields, catalog]);

    const callCtx = useMemo(() => callContextAt(formula, cursor), [formula, cursor]);
    const sigEntry = callCtx ? catalogIndex[fnKey(callCtx.name)] : null;
    const popupsVisible = Boolean(sigEntry) || completion.items.length > 0;

    useEffect(() => { setActiveItem(0); }, [completion.ctx?.query, completion.ctx?.mode]);

    useLayoutEffect(() => {
        const el = textareaRef.current;
        if (!el || !popupsVisible) {
            setCaret(null);
            return undefined;
        }
        const place = () => setCaret(caretPoint(el, completion.ctx && completion.items.length ? completion.ctx.start : cursor));
        place();
        window.addEventListener('resize', place);
        window.addEventListener('scroll', place, true);
        return () => {
            window.removeEventListener('resize', place);
            window.removeEventListener('scroll', place, true);
        };
    }, [popupsVisible, cursor, formula, completion.ctx, completion.items.length]);

    useLayoutEffect(() => {
        setSigHeight(sigRef.current ? sigRef.current.offsetHeight : 0);
    }, [sigEntry, callCtx?.argIndex, caret]);

    const syncCursor = () => {
        const el = textareaRef.current;
        if (el) setCursor(el.selectionStart || 0);
    };

    const insertAtCursor = (text, caretOffset) => {
        const el = textareaRef.current;
        if (!el) return;
        replaceRange(el, el.selectionStart, el.selectionEnd, text, caretOffset);
        setCursor(el.selectionStart);
    };

    const insertEntry = (entry) => {
        const { text, caret: at } = insertionFor(entry);
        const el = textareaRef.current;
        const before = el ? el.value.slice(0, el.selectionStart) : '';
        const pad = before && !/[\s([{,]$/.test(before) ? ' ' : '';
        insertAtCursor(pad + text, pad.length + at);
        setCompletionOpen(false);
    };

    const insertField = (field) => {
        const el = textareaRef.current;
        const before = el ? el.value.slice(0, el.selectionStart) : '';
        const pad = before && /[\p{L}\p{N}_\])]$/u.test(before) ? ' ' : '';
        insertAtCursor(pad + fieldReference(field.name));
        setCompletionOpen(false);
    };

    const acceptCompletion = (item) => {
        const el = textareaRef.current;
        const ctx = completion.ctx;
        if (!el || !item || !ctx) return;
        const from = ctx.start;
        let to = el.selectionStart;
        if (item.kind === 'field') {
            if (el.value[to] === ']') to += 1;
            replaceRange(el, from, to, fieldReference(item.field.name));
        } else if (item.kind === 'kw') {
            replaceRange(el, from, to, `${item.label} `);
        } else {
            const { text, caret: at } = insertionFor(item.entry);
            replaceRange(el, from, to, text, at);
        }
        setCursor(el.selectionStart);
        setCompletionOpen(false);
        setForcedCompletion(false);
    };

    const handleKeyDown = (event) => {
        const el = event.currentTarget;
        const items = completion.items;
        if (completionOpen && items.length) {
            if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                event.preventDefault();
                const step = event.key === 'ArrowDown' ? 1 : -1;
                setActiveItem(i => (i + step + items.length) % items.length);
                return;
            }
            if ((event.key === 'Enter' && !event.ctrlKey && !event.metaKey) || event.key === 'Tab') {
                event.preventDefault();
                acceptCompletion(items[activeItem] || items[0]);
                return;
            }
        }
        if (event.key === 'Escape') {
            event.stopPropagation();
            if (completionOpen) {
                setCompletionOpen(false);
                setForcedCompletion(false);
            } else if (!dirty) {
                onClose();
            }
            return;
        }
        if (event.key === ' ' && (event.ctrlKey || event.metaKey)) {
            event.preventDefault();
            setForcedCompletion(true);
            setCompletionOpen(true);
            return;
        }
        if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
            event.preventDefault();
            submit();
            return;
        }
        const { selectionStart: start, selectionEnd: end, value } = el;
        if (event.key === 'Tab' && !event.shiftKey) {
            event.preventDefault();
            replaceRange(el, start, end, '  ');
            return;
        }
        const next = value[start] || '';
        if (PAIRS[event.key] && start === end && (!next || /[\s)\]},]/.test(next))) {
            event.preventDefault();
            replaceRange(el, start, end, event.key + PAIRS[event.key], 1);
            setCursor(start + 1);
            if (event.key === '[') setCompletionOpen(true);
            return;
        }
        if ([')', ']', '}'].includes(event.key) && start === end && next === event.key) {
            event.preventDefault();
            el.setSelectionRange(start + 1, start + 1);
            setCursor(start + 1);
            return;
        }
        if (event.key === 'Backspace' && start === end && start > 0 && PAIRS[value[start - 1]] === next) {
            event.preventDefault();
            replaceRange(el, start - 1, start + 1, '');
        }
    };

    const handleChange = (event) => {
        setFormula(event.target.value);
        setCursor(event.target.selectionStart || 0);
        setSaveError('');
        const type = event.nativeEvent?.inputType || '';
        if (type.startsWith('insert') || type.startsWith('delete')) setCompletionOpen(true);
        if (!type.startsWith('insert')) setForcedCompletion(false);
    };

    const dirty = name !== (initial?.name || '') || formula !== (initial?.formula || '');

    function submit() {
        const cleanName = name.trim().replace(/\s+/g, ' ');
        if (!cleanName) {
            setSaveError('Ponle un nombre al campo.');
            return;
        }
        if (takenNames.some(taken => String(taken).toLowerCase() === cleanName.toLowerCase())) {
            setSaveError(`Ya existe un campo llamado «${cleanName}». Usa otro nombre.`);
            return;
        }
        if (!formula.trim()) {
            setSaveError('Escribe la fórmula.');
            return;
        }
        if (validation.status === 'error') {
            setSaveError('Corrige el error de la fórmula antes de guardar.');
            return;
        }
        const type = resultType && resultType !== 'any' ? resultType : (initial?.inferred_type || 'number');
        onSave({
            id: initial?.id || `calc-${Date.now()}`,
            name: cleanName,
            formula: formula.trim(),
            level,
            inferred_type: level === 'agg' && !resultType ? 'number' : type,
            defaultAgg,
            aggregations: afterGrouping && !hasAggregates ? aggregations : {},
        });
    }

    // ── Panel lateral ──
    const q = panelSearch.trim().toLowerCase();
    const functionGroups = useMemo(() => {
        if (!catalog) return [];
        return catalog.categories.map(category => ({
            ...category,
            entries: catalog.functions.filter(entry => entry.category === category.id && (!q
                || entry.name.toLowerCase().includes(q)
                || (entry.aliases || []).some(alias => alias.toLowerCase().includes(q))
                || String(entry.description || '').toLowerCase().includes(q))),
        })).filter(group => group.entries.length);
    }, [catalog, q]);
    const fieldMatches = (field) => !q
        || field.name.toLowerCase().includes(q)
        || String(field.label || '').toLowerCase().includes(q)
        || originText(field.origins).toLowerCase().includes(q);
    const fieldGroups = (() => {
        const shown = fields.filter(fieldMatches);
        const calcGroup = { key: 'calc', label: GROUP_LABEL.calc, items: shown.filter(f => f.group === 'calc') };
        if (!source?.isJoin) {
            return [
                ...['dimension', 'metric'].map(group => ({
                    key: group, label: GROUP_LABEL[group], items: shown.filter(f => f.group === group),
                })),
                calcGroup,
            ].filter(group => group.items.length);
        }
        // Unión: un grupo por hoja; las claves que están en ambas hojas van aparte.
        const columns = shown.filter(f => f.group !== 'calc');
        const bySheet = (source.sheets || []).map(sheet => ({
            key: sheet.id,
            label: sheet.label,
            origin: sheet,
            items: columns.filter(f => (f.origins || []).length === 1 && f.origins[0].id === sheet.id),
        }));
        const shared = { key: 'shared', label: 'En ambas hojas (claves de unión)', items: columns.filter(f => (f.origins || []).length > 1) };
        const unknown = { key: 'unknown', label: 'Otros campos de la unión', items: columns.filter(f => !(f.origins || []).length) };
        return [...bySheet, shared, unknown, calcGroup].filter(group => group.items.length);
    })();
    const shownDetail = detail || sigEntry;

    const statusLine = (() => {
        if (validation.status === 'idle') {
            return <span style={ui.muted}>Escribe la fórmula. <kbd style={ui.kbd}>Ctrl</kbd>+<kbd style={ui.kbd}>Espacio</kbd> muestra sugerencias.</span>;
        }
        if (validation.status === 'checking') return <span style={ui.muted}>Revisando…</span>;
        if (validation.status === 'offline') {
            return <span style={ui.warnText}>No se pudo validar ahora; el motor revisará la fórmula al usarla.</span>;
        }
        if (validation.status === 'error') {
            const err = result.error;
            return (
                <span style={ui.errorText} role="alert">
                    ✕ {err.message} <span style={ui.errorWhere}>· línea {err.line}, columna {err.column}</span>
                </span>
            );
        }
        const lod = (result.lod || []).map(kind => kind.toUpperCase()).join(', ');
        return (
            <span style={ui.okText}>
                ✓ Fórmula válida
                <span style={ui.statusChip}>{TYPE_LABEL[result.type] || 'Mixto'}</span>
                <span style={ui.statusChip}>{level === 'agg' ? 'Después de agrupar' : 'Por fila'}</span>
                {lod && <span style={ui.statusChip}>{lod}</span>}
            </span>
        );
    })();

    const completionTop = caret
        ? caret.top + caret.lineHeight + 4 + (sigEntry && caret.top - caret.editorTop < 48 ? sigHeight + 6 : 0)
        : 0;
    const sigBelow = caret && caret.top - caret.editorTop < 48;

    return (
        <div style={ui.overlay} onMouseDown={() => { if (!dirty) onClose(); }}>
            <div
                style={ui.sheet}
                role="dialog"
                aria-modal="true"
                aria-labelledby={titleId}
                onMouseDown={event => event.stopPropagation()}
            >
                <header style={ui.head}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={studio.kicker} id={titleId}>{editing ? 'Editar campo calculado' : 'Nuevo campo calculado'}</p>
                        <input
                            value={name}
                            onChange={event => { setName(event.target.value); setSaveError(''); }}
                            placeholder="Nombre del campo, p. ej. Clics web"
                            style={ui.nameInput}
                            aria-label="Nombre del campo"
                        />
                        {source && (
                            <div style={ui.sourceLine}>
                                <span style={ui.sourceLabel}>{source.isJoin ? 'Unión' : 'Fuente'}</span>
                                <span style={ui.sourceName}>{source.label}</span>
                                {source.isJoin && source.sheets.map(sheet => (
                                    <span key={sheet.id} style={{ ...ui.sheetChip, backgroundColor: sheet.fill, color: sheet.text }}>
                                        <span style={{ ...ui.originDot, backgroundColor: sheet.color }} />
                                        {sheet.label}
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>
                    <button type="button" style={ui.iconButton} onClick={onClose} aria-label="Cerrar">✕</button>
                </header>

                <div style={ui.body}>
                    <main style={ui.main}>
                        <div style={ui.editorWrap}>
                            <pre ref={highlightRef} aria-hidden="true" style={ui.highlight}>
                                {renderHighlighted(formula, errorRange)}
                            </pre>
                            <textarea
                                ref={textareaRef}
                                value={formula}
                                onChange={handleChange}
                                onKeyDown={handleKeyDown}
                                onSelect={syncCursor}
                                onClick={() => { syncCursor(); setCompletionOpen(false); }}
                                onBlur={() => setCompletionOpen(false)}
                                onScroll={event => {
                                    if (highlightRef.current) {
                                        highlightRef.current.scrollTop = event.currentTarget.scrollTop;
                                        highlightRef.current.scrollLeft = event.currentTarget.scrollLeft;
                                    }
                                }}
                                spellCheck={false}
                                autoCapitalize="off"
                                autoComplete="off"
                                aria-label="Fórmula"
                                aria-autocomplete="list"
                                aria-controls="formula-completions"
                                aria-activedescendant={completion.items.length ? `formula-opt-${activeItem}` : undefined}
                                placeholder={'IF CONTAINS([Campaña], "web") THEN [Clics] ELSE 0 END'}
                                style={ui.textarea}
                            />
                        </div>
                        <div style={ui.status}>{statusLine}</div>
                        {validation.status === 'ok' && (result.warnings || []).map((warning, index) => (
                            <div key={index} style={ui.warning}>⚠ {warning.message}</div>
                        ))}

                        <section style={ui.options}>
                            {level === 'row' && (resultType === 'number' || !resultType) && (
                                <label style={ui.optionLabel}>
                                    Al agrupar o en totales
                                    <select value={defaultAgg} onChange={event => setDefaultAgg(event.target.value)} style={studio.controlCompact}>
                                        {AGG_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                                    </select>
                                </label>
                            )}
                            {!hasAggregates && (
                                <label style={ui.checkLabel} title="Forma anterior de los ratios: cada campo se agrega antes de aplicar la fórmula.">
                                    <input type="checkbox" checked={afterGrouping} onChange={event => setAfterGrouping(event.target.checked)} />
                                    Calcular después de agrupar (ratio)
                                </label>
                            )}
                            {aggCalls.length > 0 && (
                                <div style={ui.aggList}>
                                    <span style={ui.optionTitle}>Agregación de cada función</span>
                                    {aggCalls.map((call, index) => (
                                        <label key={`${call.column}-${index}`} style={ui.aggRow}>
                                            <span style={ui.aggName}>{call.column}</span>
                                            <select
                                                value={call.agg}
                                                onChange={event => setFormula(prev => replaceNthAggFn(prev, index, AGG_FN_FOR[event.target.value] || 'SUM'))}
                                                style={studio.controlCompact}
                                            >
                                                {AGG_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                                            </select>
                                        </label>
                                    ))}
                                </div>
                            )}
                            {bareRefs.length > 0 && (
                                <div style={ui.aggList}>
                                    <span style={ui.optionTitle}>Agregación de cada campo</span>
                                    {bareRefs.map(ref => (
                                        <label key={ref} style={ui.aggRow}>
                                            <span style={ui.aggName}>{ref}</span>
                                            <select
                                                value={aggregations[ref] || 'sum'}
                                                onChange={event => setAggregations(prev => ({ ...prev, [ref]: event.target.value }))}
                                                style={studio.controlCompact}
                                            >
                                                {AGG_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                                            </select>
                                        </label>
                                    ))}
                                </div>
                            )}
                        </section>

                        <section style={ui.previewBox}>
                            <div style={ui.previewHead}>
                                <span style={ui.optionTitle}>Vista previa</span>
                                {level === 'agg' && dimensionFields.length > 0 && (
                                    <label style={ui.previewBy}>
                                        Desglosar por
                                        <select value={previewBy} onChange={event => setPreviewBy(event.target.value)} style={studio.controlCompact}>
                                            <option value="">Total</option>
                                            {dimensionFields.map(field => (
                                                <option key={field.name} value={field.name}>{field.label || field.name}</option>
                                            ))}
                                        </select>
                                    </label>
                                )}
                                <button
                                    type="button"
                                    style={ui.ghost}
                                    onClick={runPreview}
                                    disabled={validation.status !== 'ok' || !previewSource || preview.status === 'loading'}
                                >
                                    {preview.status === 'loading' ? 'Calculando…' : 'Actualizar'}
                                </button>
                            </div>
                            {!previewSource && <p style={ui.muted}>Elige una fuente de datos para ver la vista previa.</p>}
                            {previewSource && validation.status !== 'ok' && !preview.data && (
                                <p style={ui.muted}>Aparece cuando la fórmula es válida.</p>
                            )}
                            {preview.status === 'error' && <p style={ui.errorText}>{preview.error}</p>}
                            {preview.data && (
                                <div style={{ ...ui.previewTableWrap, opacity: preview.status === 'loading' ? 0.55 : 1 }}>
                                    <table style={ui.table}>
                                        <thead>
                                            <tr>
                                                {preview.data.columns.map((col, index) => (
                                                    <th key={col} style={index === preview.data.columns.length - 1 ? ui.thResult : ui.th}>{col}</th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {preview.data.records.map((row, rowIndex) => (
                                                <tr key={rowIndex}>
                                                    {preview.data.columns.map((col, index) => (
                                                        <td key={col} style={index === preview.data.columns.length - 1 ? ui.tdResult : ui.td}>
                                                            {formatCell(row[col])}
                                                        </td>
                                                    ))}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                    {preview.data.level === 'row' && preview.data.stats && (
                                        <p style={ui.previewStats}>
                                            {preview.data.stats.rows.toLocaleString('es-MX')} filas ·{' '}
                                            {preview.data.stats.distinct.toLocaleString('es-MX')} resultados distintos ·{' '}
                                            {preview.data.stats.nulls.toLocaleString('es-MX')} vacíos. Se muestra primero una fila por cada resultado distinto.
                                        </p>
                                    )}
                                </div>
                            )}
                        </section>
                    </main>

                    <aside style={ui.panel}>
                        <div style={ui.tabs} role="tablist">
                            {[['functions', 'Funciones'], ['fields', 'Campos']].map(([id, label]) => (
                                <button
                                    key={id}
                                    type="button"
                                    role="tab"
                                    aria-selected={panelTab === id}
                                    style={panelTab === id ? ui.tabActive : ui.tab}
                                    onClick={() => setPanelTab(id)}
                                >
                                    {label}
                                </button>
                            ))}
                        </div>
                        <input
                            value={panelSearch}
                            onChange={event => setPanelSearch(event.target.value)}
                            placeholder={panelTab === 'functions' ? 'Buscar función…' : 'Buscar campo…'}
                            style={ui.search}
                            aria-label="Buscar"
                        />
                        <div style={ui.panelList}>
                            {panelTab === 'functions' && !catalog && !catalogError && <p style={ui.muted}>Cargando funciones…</p>}
                            {panelTab === 'functions' && catalogError && (
                                <p style={ui.errorText}>
                                    {catalogError}{' '}
                                    <button type="button" style={ui.linkButton} onClick={loadCatalog}>Reintentar</button>
                                </p>
                            )}
                            {panelTab === 'functions' && functionGroups.map(group => (
                                <div key={group.id} style={ui.group}>
                                    <div style={ui.groupLabel}>{group.label}</div>
                                    {group.entries.map(entry => (
                                        <button
                                            key={entry.name}
                                            type="button"
                                            style={detail?.name === entry.name ? ui.itemActive : ui.item}
                                            onMouseEnter={() => setDetail(entry)}
                                            onFocus={() => setDetail(entry)}
                                            onMouseDown={event => event.preventDefault()}
                                            onClick={() => insertEntry(entry)}
                                            title="Insertar en la fórmula"
                                        >
                                            <span style={ui.itemName}>{entry.name}</span>
                                            <span style={ui.itemDesc}>{entry.description}</span>
                                        </button>
                                    ))}
                                </div>
                            ))}
                            {panelTab === 'fields' && fieldGroups.map(group => (
                                <div key={group.key} style={ui.group}>
                                    <div style={group.origin ? { ...ui.groupLabel, color: group.origin.text } : ui.groupLabel}>
                                        {group.origin && <span style={{ ...ui.originDot, backgroundColor: group.origin.color, marginRight: 6 }} />}
                                        {group.label}
                                    </div>
                                    {group.items.map(field => {
                                        const from = originText(field.origins);
                                        const detailText = field.group === 'calc'
                                            ? (from ? `usa ${from}` : 'calculado')
                                            : [field.label && field.label !== field.name ? field.name : '', !group.origin ? from : '']
                                                .filter(Boolean).join(' · ');
                                        return (
                                            <button
                                                key={field.name}
                                                type="button"
                                                style={ui.item}
                                                onMouseDown={event => event.preventDefault()}
                                                onClick={() => insertField(field)}
                                                title={`Insertar ${fieldReference(field.name)}${from ? ` · de ${from}` : ''}`}
                                            >
                                                <span style={ui.fieldRow}>
                                                    <span style={ui.typeBadge}>{TYPE_BADGE[field.type] || '·'}</span>
                                                    <span style={ui.itemName}>{field.label || field.name}</span>
                                                    {group.origin && ROLE_TAG[field.group] && <span style={ui.roleTag}>{ROLE_TAG[field.group]}</span>}
                                                    {!group.origin && <OriginDots origins={field.origins} />}
                                                </span>
                                                {detailText && <span style={ui.itemDesc}>{detailText}</span>}
                                            </button>
                                        );
                                    })}
                                </div>
                            ))}
                            {panelTab === 'fields' && fieldGroups.length === 0 && <p style={ui.muted}>Sin campos que coincidan.</p>}
                        </div>
                        {panelTab === 'functions' && shownDetail && (
                            <div style={ui.detail}>
                                <div style={ui.detailSig}>{shownDetail.syntax || `${shownDetail.name}(${(shownDetail.params || []).map(p => (p.optional ? `[${p.name}]` : p.name) + (p.variadic ? '…' : '')).join(', ')})`}</div>
                                <div style={ui.detailDesc}>{shownDetail.description}</div>
                                {(shownDetail.params || []).length > 0 && (
                                    <ul style={ui.paramList}>
                                        {shownDetail.params.map(param => (
                                            <li key={param.name}><strong>{param.name}</strong>{param.optional ? ' (opcional)' : ''}: {param.desc}</li>
                                        ))}
                                    </ul>
                                )}
                                {shownDetail.example && (
                                    <div style={ui.example}>
                                        <code style={ui.exampleCode}>{shownDetail.example}</code>
                                        <button
                                            type="button"
                                            style={ui.linkButton}
                                            onMouseDown={event => event.preventDefault()}
                                            onClick={() => insertAtCursor(shownDetail.example)}
                                        >
                                            Usar ejemplo
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}
                    </aside>
                </div>

                <footer style={ui.foot}>
                    <span style={ui.muted}>
                        <kbd style={ui.kbd}>Ctrl</kbd>+<kbd style={ui.kbd}>Enter</kbd> guarda · Campos entre corchetes: [Clics] · Texto entre comillas: "web"
                    </span>
                    {saveError && <span style={ui.errorText} role="alert">{saveError}</span>}
                    <div style={ui.footActions}>
                        <button type="button" style={ui.ghost} onClick={onClose}>Cancelar</button>
                        <button type="button" style={ui.primary} onClick={submit}>{editing ? 'Guardar cambios' : 'Crear campo'}</button>
                    </div>
                </footer>
            </div>

            {caret && sigEntry && createPortal(
                <div
                    ref={sigRef}
                    style={{
                        ...ui.sigPopup,
                        left: Math.min(caret.left, window.innerWidth - 380),
                        top: sigBelow ? caret.top + caret.lineHeight + 4 : caret.top - 6,
                        transform: sigBelow ? 'none' : 'translateY(-100%)',
                    }}
                    role="tooltip"
                >
                    <Signature entry={sigEntry} argIndex={callCtx.argIndex} />
                </div>,
                document.body
            )}
            {caret && completion.items.length > 0 && createPortal(
                <div
                    id="formula-completions"
                    role="listbox"
                    style={{ ...ui.completion, left: Math.min(caret.left, window.innerWidth - 340), top: completionTop }}
                    onMouseDown={event => event.preventDefault()}
                >
                    {completion.items.map((item, index) => (
                        <div
                            key={item.key}
                            id={`formula-opt-${index}`}
                            role="option"
                            aria-selected={index === activeItem}
                            style={index === activeItem ? ui.optionActive : ui.option}
                            onMouseEnter={() => setActiveItem(index)}
                            onClick={() => acceptCompletion(item)}
                        >
                            <span style={ui.optionKind(item.kind)}>
                                {item.kind === 'field' ? TYPE_BADGE[item.field.type] || '·' : item.kind === 'kw' ? 'kw' : 'ƒ'}
                            </span>
                            <span style={ui.optionName}>{item.label}</span>
                            {item.kind === 'field' && <OriginDots origins={item.field.origins} />}
                            <span style={ui.optionDetail}>{item.detail}</span>
                        </div>
                    ))}
                    <div style={ui.completionHint}>↑↓ elegir · Enter o Tab insertar · Esc cerrar</div>
                </div>,
                document.body
            )}
        </div>
    );
};

const editorText = {
    fontFamily: MONO,
    fontSize: 13.5,
    lineHeight: '21px',
    letterSpacing: 0,
    tabSize: 2,
    whiteSpace: 'pre-wrap',
    wordWrap: 'break-word',
    overflowWrap: 'break-word',
    paddingTop: 12,
    paddingRight: 14,
    paddingBottom: 12,
    paddingLeft: 14,
    boxSizing: 'border-box',
    margin: 0,
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: 'transparent',
};

const itemBase = {
    appearance: 'none',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 1,
    width: '100%',
    textAlign: 'left',
    paddingTop: 6,
    paddingBottom: 6,
    paddingLeft: 8,
    paddingRight: 8,
    borderWidth: 0,
    borderRadius: 8,
    backgroundColor: 'transparent',
    cursor: 'pointer',
    fontFamily: 'inherit',
};

const optionBase = {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    paddingTop: 5,
    paddingBottom: 5,
    paddingLeft: 8,
    paddingRight: 8,
    cursor: 'pointer',
    fontSize: '0.8rem',
    backgroundColor: ink.surface,
};

const ui = {
    overlay: {
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(28, 29, 33, 0.5)',
        zIndex: 62,
        display: 'flex',
        alignItems: 'stretch',
        justifyContent: 'center',
        padding: 16,
    },
    sheet: {
        width: 'min(1180px, 100%)',
        height: 'min(92vh, 900px)',
        backgroundColor: ink.surface,
        borderRadius: 18,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        boxShadow: '0 28px 80px rgba(28, 29, 33, 0.28)',
    },
    head: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: 12,
        padding: '16px 18px 12px',
        borderBottomWidth: 1,
        borderBottomStyle: 'solid',
        borderBottomColor: ink.line,
        flexShrink: 0,
    },
    nameInput: {
        marginTop: 4,
        width: '100%',
        maxWidth: 520,
        fontSize: '1.2rem',
        fontWeight: 600,
        color: ink.text,
        fontFamily: 'inherit',
        paddingTop: 4,
        paddingBottom: 4,
        paddingLeft: 0,
        paddingRight: 0,
        borderTopWidth: 0,
        borderRightWidth: 0,
        borderLeftWidth: 0,
        borderBottomWidth: 2,
        borderStyle: 'solid',
        borderColor: ink.line,
        outline: 'none',
        backgroundColor: 'transparent',
    },
    iconButton: {
        appearance: 'none',
        width: 34,
        height: 34,
        borderRadius: 999,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: ink.lineStrong,
        backgroundColor: ink.surface,
        color: ink.muted,
        cursor: 'pointer',
        fontSize: '0.95rem',
        flexShrink: 0,
    },
    body: {
        flex: 1,
        minHeight: 0,
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr) minmax(260px, 320px)',
    },
    main: {
        minWidth: 0,
        overflow: 'auto',
        padding: 18,
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
    },
    editorWrap: {
        position: 'relative',
        borderRadius: 12,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: ink.lineStrong,
        backgroundColor: '#fbfbfd',
        flexShrink: 0,
    },
    highlight: {
        ...editorText,
        position: 'absolute',
        inset: 0,
        overflow: 'hidden',
        pointerEvents: 'none',
        color: ink.text,
    },
    textarea: {
        ...editorText,
        position: 'relative',
        display: 'block',
        width: '100%',
        minHeight: 168,
        resize: 'vertical',
        backgroundColor: 'transparent',
        color: 'transparent',
        WebkitTextFillColor: 'transparent',
        caretColor: ink.text,
        outline: 'none',
        borderRadius: 12,
    },
    errorMark: {
        textDecorationLine: 'underline',
        textDecorationStyle: 'wavy',
        textDecorationColor: ink.danger,
        textDecorationSkipInk: 'none',
        textUnderlineOffset: 3,
        backgroundColor: 'rgba(197, 34, 31, 0.08)',
    },
    status: { minHeight: 22, fontSize: '0.82rem', display: 'flex', alignItems: 'center' },
    okText: { color: '#137333', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' },
    statusChip: {
        fontWeight: 600,
        fontSize: '0.72rem',
        color: ink.accentText,
        backgroundColor: ink.accentSoft,
        borderRadius: 999,
        paddingTop: 2,
        paddingBottom: 2,
        paddingLeft: 8,
        paddingRight: 8,
    },
    errorText: { color: ink.danger, fontSize: '0.82rem', fontWeight: 600, margin: 0 },
    errorWhere: { fontWeight: 400, color: ink.muted },
    warnText: { color: '#b06000', fontSize: '0.82rem' },
    warning: {
        fontSize: '0.78rem',
        color: '#7a4b00',
        backgroundColor: '#fef7e0',
        borderRadius: 8,
        paddingTop: 6,
        paddingBottom: 6,
        paddingLeft: 10,
        paddingRight: 10,
    },
    muted: { color: ink.muted, fontSize: '0.8rem', margin: 0 },
    kbd: {
        fontFamily: MONO,
        fontSize: '0.72rem',
        paddingTop: 1,
        paddingBottom: 1,
        paddingLeft: 5,
        paddingRight: 5,
        borderRadius: 4,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: ink.lineStrong,
        backgroundColor: ink.wash,
    },
    options: { display: 'flex', flexWrap: 'wrap', gap: 14, alignItems: 'flex-start' },
    optionLabel: { display: 'flex', flexDirection: 'column', gap: 4, fontSize: '0.72rem', fontWeight: 700, color: ink.muted },
    optionTitle: { fontSize: '0.72rem', fontWeight: 700, color: ink.muted, textTransform: 'uppercase', letterSpacing: '0.05em' },
    checkLabel: { display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.8rem', color: ink.text, alignSelf: 'center' },
    aggList: { display: 'flex', flexDirection: 'column', gap: 6, minWidth: 220 },
    aggRow: { display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.8rem' },
    aggName: { flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: 600 },
    previewBox: {
        borderRadius: 12,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: ink.line,
        padding: 12,
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
    },
    previewHead: { display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' },
    previewBy: { display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.78rem', color: ink.muted, marginLeft: 'auto' },
    previewTableWrap: { overflowX: 'auto', transition: 'opacity 120ms' },
    table: { width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' },
    th: {
        textAlign: 'left',
        fontWeight: 600,
        color: ink.muted,
        paddingTop: 6,
        paddingBottom: 6,
        paddingLeft: 8,
        paddingRight: 8,
        borderBottomWidth: 1,
        borderBottomStyle: 'solid',
        borderBottomColor: ink.line,
        whiteSpace: 'nowrap',
    },
    thResult: {
        textAlign: 'left',
        fontWeight: 700,
        color: ink.accentText,
        backgroundColor: ink.accentSoft,
        paddingTop: 6,
        paddingBottom: 6,
        paddingLeft: 8,
        paddingRight: 8,
        borderBottomWidth: 1,
        borderBottomStyle: 'solid',
        borderBottomColor: ink.line,
        whiteSpace: 'nowrap',
    },
    td: {
        paddingTop: 5,
        paddingBottom: 5,
        paddingLeft: 8,
        paddingRight: 8,
        borderBottomWidth: 1,
        borderBottomStyle: 'solid',
        borderBottomColor: ink.line,
        color: ink.text,
        maxWidth: 260,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    tdResult: {
        paddingTop: 5,
        paddingBottom: 5,
        paddingLeft: 8,
        paddingRight: 8,
        borderBottomWidth: 1,
        borderBottomStyle: 'solid',
        borderBottomColor: ink.line,
        backgroundColor: '#faf7ff',
        fontWeight: 600,
        color: ink.text,
        whiteSpace: 'nowrap',
    },
    nullCell: { color: ink.faint, fontStyle: 'italic', fontWeight: 400 },
    previewStats: { margin: '6px 0 0', fontSize: '0.74rem', color: ink.muted },
    panel: {
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        padding: 12,
        backgroundColor: ink.wash,
        borderLeftWidth: 1,
        borderLeftStyle: 'solid',
        borderLeftColor: ink.line,
    },
    tabs: { display: 'flex', gap: 4, backgroundColor: ink.line, borderRadius: 999, padding: 3 },
    tab: {
        flex: 1,
        appearance: 'none',
        borderWidth: 0,
        borderRadius: 999,
        paddingTop: 6,
        paddingBottom: 6,
        backgroundColor: 'transparent',
        color: ink.muted,
        fontWeight: 600,
        cursor: 'pointer',
        fontFamily: 'inherit',
    },
    tabActive: {
        flex: 1,
        appearance: 'none',
        borderWidth: 0,
        borderRadius: 999,
        paddingTop: 6,
        paddingBottom: 6,
        backgroundColor: ink.surface,
        color: ink.accentText,
        fontWeight: 700,
        cursor: 'pointer',
        fontFamily: 'inherit',
        boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
    },
    search: { ...studio.controlCompact, width: '100%', boxSizing: 'border-box' },
    panelList: { flex: 1, minHeight: 0, overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 10 },
    group: { display: 'flex', flexDirection: 'column', gap: 1 },
    groupLabel: {
        fontSize: '0.65rem',
        fontWeight: 700,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        color: ink.faint,
        paddingTop: 2,
        paddingBottom: 2,
        paddingLeft: 8,
    },
    item: itemBase,
    itemActive: { ...itemBase, backgroundColor: ink.surface, boxShadow: `inset 3px 0 0 ${ink.accent}` },
    itemName: { fontFamily: MONO, fontSize: '0.8rem', fontWeight: 700, color: ink.text },
    itemDesc: {
        fontSize: '0.72rem',
        color: ink.muted,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        maxWidth: '100%',
    },
    fieldRow: { display: 'flex', alignItems: 'center', gap: 6, minWidth: 0, width: '100%' },
    originDot: { display: 'inline-block', width: 8, height: 8, borderRadius: 999, flexShrink: 0 },
    roleTag: { fontSize: '0.62rem', fontWeight: 700, color: ink.faint, marginLeft: 'auto', flexShrink: 0 },
    sourceLine: { display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginTop: 8, fontSize: '0.78rem' },
    sourceLabel: { fontWeight: 700, color: ink.faint, textTransform: 'uppercase', letterSpacing: '0.06em', fontSize: '0.66rem' },
    sourceName: { fontWeight: 600, color: ink.text },
    sheetChip: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        borderRadius: 999,
        paddingTop: 2,
        paddingBottom: 2,
        paddingLeft: 8,
        paddingRight: 8,
        fontWeight: 600,
        fontSize: '0.72rem',
    },
    typeBadge: {
        fontFamily: MONO,
        fontSize: '0.62rem',
        fontWeight: 700,
        color: ink.accentText,
        backgroundColor: ink.accentSoft,
        borderRadius: 4,
        paddingTop: 1,
        paddingBottom: 1,
        paddingLeft: 4,
        paddingRight: 4,
        flexShrink: 0,
    },
    detail: {
        flexShrink: 0,
        backgroundColor: ink.surface,
        borderRadius: 10,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: ink.line,
        padding: 10,
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
        maxHeight: '42%',
        overflow: 'auto',
    },
    detailSig: { fontFamily: MONO, fontSize: '0.78rem', fontWeight: 700, color: ink.accentText, wordBreak: 'break-word' },
    detailDesc: { fontSize: '0.78rem', color: ink.text, lineHeight: 1.4 },
    paramList: { margin: 0, paddingLeft: 16, fontSize: '0.74rem', color: ink.muted, lineHeight: 1.45 },
    example: { display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4 },
    exampleCode: {
        fontFamily: MONO,
        fontSize: '0.74rem',
        backgroundColor: ink.wash,
        borderRadius: 6,
        paddingTop: 4,
        paddingBottom: 4,
        paddingLeft: 6,
        paddingRight: 6,
        wordBreak: 'break-word',
        whiteSpace: 'pre-wrap',
    },
    linkButton: {
        appearance: 'none',
        borderWidth: 0,
        backgroundColor: 'transparent',
        color: ink.accent,
        fontWeight: 700,
        cursor: 'pointer',
        padding: 0,
        fontFamily: 'inherit',
        fontSize: '0.76rem',
    },
    foot: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '12px 18px',
        borderTopWidth: 1,
        borderTopStyle: 'solid',
        borderTopColor: ink.line,
        flexShrink: 0,
        flexWrap: 'wrap',
    },
    footActions: { display: 'flex', gap: 8, marginLeft: 'auto' },
    ghost: {
        appearance: 'none',
        padding: '7px 14px',
        borderRadius: 999,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: ink.lineStrong,
        backgroundColor: ink.surface,
        color: ink.accentText,
        cursor: 'pointer',
        fontWeight: 700,
        fontFamily: 'inherit',
    },
    primary: {
        appearance: 'none',
        padding: '8px 16px',
        borderRadius: 999,
        borderWidth: 0,
        backgroundColor: ink.accent,
        color: ink.surface,
        cursor: 'pointer',
        fontWeight: 700,
        fontFamily: 'inherit',
    },
    sigPopup: {
        position: 'fixed',
        zIndex: 70,
        maxWidth: 380,
        backgroundColor: '#202124',
        color: '#f1f3f4',
        borderRadius: 8,
        paddingTop: 8,
        paddingBottom: 8,
        paddingLeft: 10,
        paddingRight: 10,
        boxShadow: '0 6px 18px rgba(0,0,0,0.25)',
        pointerEvents: 'none',
        fontSize: '0.78rem',
        lineHeight: 1.4,
    },
    sigLine: { fontFamily: MONO, fontSize: '0.8rem' },
    sigActive: { fontWeight: 700, color: '#fdd663', textDecorationLine: 'underline', textUnderlineOffset: 3 },
    sigParam: { marginTop: 4, color: '#e8eaed' },
    sigDesc: { marginTop: 4, color: '#bdc1c6' },
    completion: {
        position: 'fixed',
        zIndex: 71,
        width: 340,
        maxHeight: 300,
        overflowY: 'auto',
        backgroundColor: ink.surface,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: '#d1c4e9',
        borderRadius: 10,
        boxShadow: '0 8px 24px rgba(0,0,0,0.14)',
    },
    option: optionBase,
    optionActive: { ...optionBase, backgroundColor: ink.accentSoft },
    optionKind: (kind) => ({
        fontFamily: MONO,
        fontSize: '0.65rem',
        fontWeight: 700,
        minWidth: 30,
        textAlign: 'center',
        borderRadius: 4,
        paddingTop: 1,
        paddingBottom: 1,
        color: kind === 'field' ? '#0b6e4f' : kind === 'kw' ? ink.accent : '#1a5fb4',
        backgroundColor: kind === 'field' ? '#e6f4ea' : kind === 'kw' ? ink.accentSoft : '#e8f0fe',
    }),
    optionName: { fontFamily: MONO, fontWeight: 600, color: ink.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 150 },
    optionDetail: { flex: 1, minWidth: 0, color: ink.muted, fontSize: '0.72rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
    completionHint: {
        fontSize: '0.66rem',
        color: ink.faint,
        paddingTop: 4,
        paddingBottom: 4,
        paddingLeft: 8,
        borderTopWidth: 1,
        borderTopStyle: 'solid',
        borderTopColor: ink.line,
        position: 'sticky',
        bottom: 0,
        backgroundColor: ink.surface,
    },
};

export default FormulaEditorModal;
